import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { crmStats, crmListLeads, crmGetLead, crmCreateLead, crmUpdateLead, crmDeleteLead, crmAddNote, crmAddTask, crmUpdateTask, crmDeleteTask, crmSync, crmListAttachments, crmUploadAttachment, crmDeleteAttachment, crmAttachmentDownloadUrl, crmGetKyc, crmSaveKyc } from '../../api';
import { toast } from '../../hooks/use-toast';
import {
  Plus, Search, Trash2, RefreshCcw, Mail, Phone, Building2, Tag,
  MessageSquare, CheckSquare, Square, Calendar, FileText, Receipt,
  User as UserIcon, ExternalLink, X, Paperclip, Upload, Download, FileDown, ShieldCheck
} from 'lucide-react';

const STAGES = [
  { id: 'new', color: '#6b7280', bg: '#f3f4f6' },
  { id: 'contacted', color: '#0ea5e9', bg: '#e0f2fe' },
  { id: 'qualified', color: '#8b5cf6', bg: '#ede9fe' },
  { id: 'quoted', color: '#f59e0b', bg: '#fef3c7' },
  { id: 'won', color: '#2563eb', bg: '#dbeafe' },
  { id: 'lost', color: '#e11d48', bg: '#ffe4e6' },
];

const SOURCE_LABEL = {
  contact_form: { en: 'Contact Form', ar: 'نموذج تواصل', color: '#0ea5e9' },
  application: { en: 'Application', ar: 'طلب تأسيس', color: '#2563eb' },
  manual: { en: 'Manual', ar: 'يدوي', color: '#6b7280' },
  referral: { en: 'Referral', ar: 'إحالة', color: '#f59e0b' },
  newsletter: { en: 'Newsletter', ar: 'نشرة', color: '#8b5cf6' },
};

const stageLabel = (id, lang) => {
  const ar = { new: 'جديد', contacted: 'تم التواصل', qualified: 'مؤهّل', quoted: 'عرض سعر', won: 'مكسوب', lost: 'مفقود' };
  const en = { new: 'New', contacted: 'Contacted', qualified: 'Qualified', quoted: 'Quoted', won: 'Won', lost: 'Lost' };
  return (lang === 'ar' ? ar : en)[id] || id;
};

const AdminCRM = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState('info');
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [newLeadDocs, setNewLeadDocs] = useState({ passport: null, emirates_id: null });
  const [newLeadForm, setNewLeadForm] = useState({
    name: '', email: '', phone: '', company: '', stage: 'new', value_estimate: 0, currency: 'AED',
    // KYC (optional)
    include_kyc: false,
    kyc_full_name: '', kyc_passport_number: '', kyc_nationality: '', kyc_date_of_birth: '',
    kyc_emirates_id: '', kyc_occupation: '', kyc_address: '', kyc_source_of_funds: '',
    kyc_check_passport: false, kyc_check_emirates_id: false, kyc_check_address: false,
    kyc_check_source_of_funds: false, kyc_check_pep_declaration: false,
    kyc_check_sanctions_manual: false, kyc_check_no_adverse_media: false,
    kyc_risk_level: '', kyc_status: 'pending', kyc_notes: ''
  });
  const [noteBody, setNoteBody] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDue, setTaskDue] = useState('');
  const [dragged, setDragged] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [kyc, setKyc] = useState(null);
  const [kycSaving, setKycSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ls, st] = await Promise.all([crmListLeads({ search: search || undefined }), crmStats()]);
      setLeads(ls || []);
      setStats(st);
    } catch (e) {
      toast({ title: lang === 'ar' ? 'فشل التحميل' : 'Failed to load', variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
      l.name?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q) ||
      l.company?.toLowerCase().includes(q) ||
      l.id?.toLowerCase().includes(q)
    );
  }, [leads, search]);

  const grouped = useMemo(() => {
    const g = {}; STAGES.forEach((s) => { g[s.id] = []; });
    filtered.forEach((l) => { if (g[l.stage]) g[l.stage].push(l); });
    return g;
  }, [filtered]);

  const openLead = async (l) => {
    setSelected(l); setTab('info'); setDetail(null); setAttachments([]); setKyc(null);
    try { setDetail(await crmGetLead(l.id)); } catch { toast({ title: 'Failed to load', variant: 'destructive' }); }
    try { const r = await crmListAttachments(l.id); setAttachments(r.attachments || []); } catch { /* ignore */ }
    try { setKyc(await crmGetKyc(l.id)); } catch { /* ignore */ }
  };

  const changeStage = async (leadId, stage) => {
    // optimistic
    setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, stage } : l));
    try {
      await crmUpdateLead(leadId, { stage });
      toast({ title: lang === 'ar' ? 'تم تحديث المرحلة' : 'Stage updated' });
      load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); load(); }
  };

  const createLead = async () => {
    if (!newLeadForm.name) { toast({ title: lang === 'ar' ? 'الاسم مطلوب' : 'Name required', variant: 'destructive' }); return; }
    try {
      const { include_kyc, ...basic } = newLeadForm;
      // Strip kyc_* prefixed keys from lead payload
      const leadPayload = Object.fromEntries(Object.entries(basic).filter(([k]) => !k.startsWith('kyc_')));
      const created = await crmCreateLead({ ...leadPayload, source: 'manual', email: newLeadForm.email || undefined });
      const newLead = created?.lead || created;
      // If admin filled KYC, persist it right after lead creation
      if (include_kyc && newLead?.id) {
        const kycPayload = {
          full_name: newLeadForm.kyc_full_name || undefined,
          passport_number: newLeadForm.kyc_passport_number || undefined,
          nationality: newLeadForm.kyc_nationality || undefined,
          date_of_birth: newLeadForm.kyc_date_of_birth || undefined,
          emirates_id: newLeadForm.kyc_emirates_id || undefined,
          occupation: newLeadForm.kyc_occupation || undefined,
          address: newLeadForm.kyc_address || undefined,
          source_of_funds: newLeadForm.kyc_source_of_funds || undefined,
          check_passport: newLeadForm.kyc_check_passport,
          check_emirates_id: newLeadForm.kyc_check_emirates_id,
          check_address: newLeadForm.kyc_check_address,
          check_source_of_funds: newLeadForm.kyc_check_source_of_funds,
          check_pep_declaration: newLeadForm.kyc_check_pep_declaration,
          check_sanctions_manual: newLeadForm.kyc_check_sanctions_manual,
          check_no_adverse_media: newLeadForm.kyc_check_no_adverse_media,
          risk_level: newLeadForm.kyc_risk_level || undefined,
          status: newLeadForm.kyc_status || 'pending',
          notes: newLeadForm.kyc_notes || undefined,
        };
        try { await crmSaveKyc(newLead.id, kycPayload); } catch { /* non-fatal */ }
        for (const [docType, file] of Object.entries(newLeadDocs)) {
          if (file) { try { await crmUploadAttachment(newLead.id, file, docType); } catch { /* non-fatal */ } }
        }
      }
      toast({ title: lang === 'ar' ? 'تم إنشاء العميل المحتمل' : 'Lead created' });
      setNewLeadOpen(false);
      setNewLeadDocs({ passport: null, emirates_id: null });
      setNewLeadForm({
        name: '', email: '', phone: '', company: '', stage: 'new', value_estimate: 0, currency: 'AED',
        include_kyc: false, kyc_full_name: '', kyc_passport_number: '', kyc_nationality: '', kyc_date_of_birth: '',
        kyc_emirates_id: '', kyc_occupation: '', kyc_address: '', kyc_source_of_funds: '',
        kyc_check_passport: false, kyc_check_emirates_id: false, kyc_check_address: false,
        kyc_check_source_of_funds: false, kyc_check_pep_declaration: false,
        kyc_check_sanctions_manual: false, kyc_check_no_adverse_media: false,
        kyc_risk_level: '', kyc_status: 'pending', kyc_notes: ''
      });
      await load();
      // Auto-open the PDF for the newly-created lead
      if (newLead?.id) {
        const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        window.open(`${base}/api/admin/crm/leads/${newLead.id}/export.pdf?_t=${Date.now()}`, '_blank');
      }
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const doSync = async () => {
    try { const r = await crmSync(); toast({ title: lang === 'ar' ? `تمت المزامنة (${r.leads} إجمالي)` : `Synced (${r.leads} total)` }); load(); }
    catch { toast({ title: 'Sync failed', variant: 'destructive' }); }
  };

  const addNote = async () => {
    if (!noteBody.trim() || !selected) return;
    try {
      await crmAddNote(selected.id, noteBody);
      setNoteBody('');
      setDetail(await crmGetLead(selected.id));
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const addTask = async () => {
    if (!taskTitle.trim() || !selected) return;
    try {
      await crmAddTask(selected.id, { title: taskTitle, due_at: taskDue || undefined });
      setTaskTitle(''); setTaskDue('');
      setDetail(await crmGetLead(selected.id));
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const toggleTask = async (t) => {
    try {
      await crmUpdateTask(selected.id, t.id, { done: !t.done });
      setDetail(await crmGetLead(selected.id));
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const removeTask = async (tid) => {
    try { await crmDeleteTask(selected.id, tid); setDetail(await crmGetLead(selected.id)); } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const uploadAttachment = async (file, docType = 'general') => {
    if (!file || !selected) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: lang === 'ar' ? 'الملف أكبر من 5 ميجا' : 'File exceeds 5 MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      await crmUploadAttachment(selected.id, file, docType);
      const r = await crmListAttachments(selected.id);
      setAttachments(r.attachments || []);
      toast({ title: lang === 'ar' ? '📎 تم رفع المرفق' : '📎 Attachment uploaded' });
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Upload failed', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const removeAttachment = async (attId) => {
    if (!window.confirm(lang === 'ar' ? 'حذف المرفق؟' : 'Delete attachment?')) return;
    try {
      await crmDeleteAttachment(selected.id, attId);
      setAttachments((prev) => prev.filter((a) => a.id !== attId));
      toast({ title: lang === 'ar' ? 'حُذف' : 'Deleted' });
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const saveKyc = async () => {
    if (!selected || !kyc) return;
    setKycSaving(true);
    try {
      const payload = { ...kyc };
      delete payload.lead_id; delete payload.created_at; delete payload.updated_at; delete payload.updated_by;
      const res = await crmSaveKyc(selected.id, payload);
      setKyc(res);
      toast({ title: lang === 'ar' ? '✅ حُفظ فحص KYC' : '✅ KYC saved' });
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setKycSaving(false); }
  };

  const exportPdf = async () => {
    // Refresh data first, then open the PDF endpoint in a new tab.
    await load();
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    window.open(`${base}/api/admin/crm/leads/export.pdf?_t=${Date.now()}`, '_blank');
    toast({ title: lang === 'ar' ? '📄 يتم توليد PDF…' : '📄 Generating PDF…' });
  };

  const removeLead = async () => {
    if (!selected) return;
    if (!window.confirm(lang === 'ar' ? 'حذف هذا العميل المحتمل؟' : 'Delete this lead?')) return;
    try { await crmDeleteLead(selected.id); setSelected(null); load(); toast({ title: lang === 'ar' ? 'حُذف' : 'Deleted' }); } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const onDragStart = (id) => setDragged(id);
  const onDrop = (stage) => {
    if (dragged) changeStage(dragged, stage);
    setDragged(null);
  };

  return (
    <div className="space-y-6" data-testid="admin-crm">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0a0a0a]">CRM · {lang === 'ar' ? 'العملاء المحتملون' : 'Leads Pipeline'}</h1>
          <p className="text-sm text-[#111827]/60 mt-2">{leads.length} {lang === 'ar' ? 'عميل محتمل' : 'leads'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={doSync} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent" title={lang === 'ar' ? 'مزامنة من الطلبات ورسائل التواصل' : 'Sync from Applications & Contacts'}>
            <RefreshCcw className="w-4 h-4 me-2" /> {lang === 'ar' ? 'مزامنة' : 'Sync'}
          </Button>
          <Button onClick={exportPdf} variant="outline" className="rounded-md h-10 px-4 text-xs border-[#2563eb]/40 text-[#2563eb] hover:bg-[#2563eb]/8" data-testid="crm-export-pdf-btn">
            <FileDown className="w-4 h-4 me-2" /> {lang === 'ar' ? 'كشف PDF' : 'Export PDF'}
          </Button>
          <Button onClick={() => setNewLeadOpen(true)} className="gold-button rounded-md h-10 px-5 text-xs">
            <Plus className="w-4 h-4 me-2" /> {lang === 'ar' ? 'عميل محتمل جديد' : 'New Lead'}
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="glass-card rounded-xl p-4"><div className="text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'مفتوحة' : 'Open'}</div><div className="text-2xl font-bold text-[#0a0a0a] mt-1">{stats.open_leads}</div></div>
          <div className="glass-card rounded-xl p-4"><div className="text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'مكسوبة' : 'Won'}</div><div className="text-2xl font-bold text-[#3b82f6] mt-1">{stats.won_leads}</div></div>
          <div className="glass-card rounded-xl p-4"><div className="text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'مهام متأخرة' : 'Overdue Tasks'}</div><div className="text-2xl font-bold text-rose-500 mt-1">{stats.overdue_tasks}</div></div>
          <div className="glass-card rounded-xl p-4"><div className="text-[10px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'قيمة الأعمال' : 'Pipeline Value'}</div>
            <div className="mt-1 space-y-0.5">
              {Object.entries(stats.by_currency || { AED: 0 }).map(([cur, val]) => (
                <div key={cur} className="text-lg font-bold text-[#0a0a0a] leading-tight">{Number(val).toLocaleString()} <span className="text-xs text-[#111827]/60 font-medium">{cur}</span></div>
              ))}
              {Object.keys(stats.by_currency || {}).length === 0 && <div className="text-lg font-bold text-[#0a0a0a]">0 AED</div>}
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="glass-card rounded-xl p-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-[#2563eb]/70 ms-2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={lang === 'ar' ? 'بحث بالاسم، البريد، الشركة، المعرّف' : 'Search by name, email, company, ID'} className="border-0 bg-transparent text-[#111827] focus-visible:ring-0" />
      </div>

      {/* Kanban board */}
      {loading ? <div className="glass-card p-10 text-center rounded-2xl text-[#111827]/60">…</div> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {STAGES.map((s) => (
            <div key={s.id} className="min-h-[500px]" data-testid={`stage-${s.id}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(s.id)}
            >
              <div className="rounded-t-xl px-3 py-2 flex items-center justify-between" style={{ background: s.bg, color: s.color }}>
                <div className="text-[10px] uppercase tracking-widest font-bold">{stageLabel(s.id, lang)}</div>
                <div className="text-xs font-bold">{grouped[s.id]?.length || 0}</div>
              </div>
              <div className="bg-white/60 rounded-b-xl p-2 space-y-2 min-h-[440px] border border-t-0 border-[#e5e7eb]">
                {(grouped[s.id] || []).map((l) => {
                  const src = SOURCE_LABEL[l.source] || SOURCE_LABEL.manual;
                  const openTasks = (l.tasks || []).filter((t) => !t.done).length;
                  return (
                    <div
                      key={l.id}
                      draggable
                      onDragStart={() => onDragStart(l.id)}
                      onClick={() => openLead(l)}
                      className="glass-card rounded-lg p-3 cursor-pointer hover:border-[#2563eb]"
                      data-testid={`lead-card-${l.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="text-sm font-semibold text-[#0a0a0a] truncate">{l.name}</div>
                        <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-widest font-bold whitespace-nowrap" style={{ background: `${src.color}18`, color: src.color }}>{src[lang]}</span>
                      </div>
                      {l.company && <div className="text-[11px] text-[#111827]/60 truncate flex items-center gap-1"><Building2 className="w-2.5 h-2.5" /> {l.company}</div>}
                      {l.email && <div className="text-[11px] text-[#111827]/50 truncate">{l.email}</div>}
                      {(l.app_type || l.zone_code) && (
                        <div className="mt-1.5 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded" style={{ background: l.app_type === 'mainland' ? '#fef3c7' : '#dbeafe', color: l.app_type === 'mainland' ? '#92400e' : '#1e40af' }}>
                          {l.app_type === 'mainland' ? (lang === 'ar' ? 'براً' : 'MAINLAND') : (lang === 'ar' ? 'منطقة حرة' : 'FREEZONE')}
                          {l.zone_code && <span className="opacity-70">· {l.zone_code.toUpperCase()}</span>}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#e5e7eb]">
                        <div className="text-[10px] text-[#2563eb] font-mono">{l.id}</div>
                        <div className="flex items-center gap-2 text-[10px] text-[#111827]/60">
                          {l.value_estimate > 0 && <span className="flex items-center gap-0.5" title={l.currency || 'AED'}>{Number(l.value_estimate).toLocaleString()} <span className="opacity-70">{l.currency || 'AED'}</span></span>}
                          {openTasks > 0 && <span className="flex items-center gap-0.5"><CheckSquare className="w-2.5 h-2.5" />{openTasks}</span>}
                          {(l.notes || []).length > 0 && <span className="flex items-center gap-0.5"><MessageSquare className="w-2.5 h-2.5" />{l.notes.length}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(grouped[s.id] || []).length === 0 && (
                  <div className="text-center text-[10px] text-[#111827]/30 py-6">{lang === 'ar' ? 'اسحب هنا' : 'Drop here'}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lead Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="bg-white border-[#e5e7eb] text-[#0a0a0a] max-w-4xl max-h-[92vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#0a0a0a] font-display text-2xl flex items-center gap-3">
                  {selected.name}
                  <span className="text-xs px-2 py-1 rounded font-mono text-[#2563eb] bg-[#2563eb]/10">{selected.id}</span>
                </DialogTitle>
              </DialogHeader>
              {/* Tabs */}
              <div className="flex gap-1 border-b border-[#e5e7eb] mb-4">
                {[
                  { id: 'info', label: lang === 'ar' ? 'معلومات' : 'Info' },
                  { id: 'timeline', label: lang === 'ar' ? 'ملاحظات' : 'Notes' },
                  { id: 'tasks', label: lang === 'ar' ? 'مهام' : 'Tasks' },
                  { id: 'attachments', label: lang === 'ar' ? 'مرفقات' : 'Attachments' },
                  { id: 'kyc', label: lang === 'ar' ? 'KYC' : 'KYC' },
                  { id: 'linked', label: lang === 'ar' ? 'سجلات مرتبطة' : 'Linked (360°)' }
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-[#2563eb] text-[#2563eb] font-semibold' : 'border-transparent text-[#111827]/60 hover:text-[#111827]'}`}
                    data-testid={`tab-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* INFO */}
              {tab === 'info' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase text-[#2563eb]">{lang === 'ar' ? 'الاسم' : 'Name'}</Label>
                      <Input value={selected.name} onChange={(e) => setSelected({ ...selected, name: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" /></div>
                    <div><Label className="text-xs uppercase text-[#2563eb]">{lang === 'ar' ? 'الشركة' : 'Company'}</Label>
                      <Input value={selected.company || ''} onChange={(e) => setSelected({ ...selected, company: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" /></div>
                    <div><Label className="text-xs uppercase text-[#2563eb]">{lang === 'ar' ? 'البريد' : 'Email'}</Label>
                      <Input type="email" value={selected.email || ''} onChange={(e) => setSelected({ ...selected, email: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" /></div>
                    <div><Label className="text-xs uppercase text-[#2563eb]">{lang === 'ar' ? 'الهاتف' : 'Phone'}</Label>
                      <Input value={selected.phone || ''} onChange={(e) => setSelected({ ...selected, phone: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" /></div>
                    <div>
                      <Label className="text-xs uppercase text-[#2563eb]">{lang === 'ar' ? 'المرحلة' : 'Stage'}</Label>
                      <Select value={selected.stage} onValueChange={(v) => setSelected({ ...selected, stage: v })}>
                        <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e5e7eb]"><SelectValue /></SelectTrigger>
                        <SelectContent>{STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{stageLabel(s.id, lang)}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs uppercase text-[#2563eb]">{lang === 'ar' ? 'القيمة المتوقعة' : 'Estimated Value'}</Label>
                      <div className="flex gap-2 mt-1">
                        <Input type="number" value={selected.value_estimate || 0} onChange={(e) => setSelected({ ...selected, value_estimate: Number(e.target.value) })} className="bg-[#f8fafc] border-[#e5e7eb] flex-1" />
                        <Select value={selected.currency || 'AED'} onValueChange={(v) => setSelected({ ...selected, currency: v })}>
                          <SelectTrigger data-testid="lead-currency-select" className="bg-[#f8fafc] border-[#e5e7eb] w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AED">AED</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-[#111827]/60 flex items-center gap-3">
                    <span>{lang === 'ar' ? 'المصدر' : 'Source'}: <b>{SOURCE_LABEL[selected.source]?.[lang] || selected.source}</b></span>
                    {selected.source_ref && <span className="font-mono">↳ {selected.source_ref}</span>}
                  </div>
                </div>
              )}

              {/* NOTES / TIMELINE */}
              {tab === 'timeline' && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder={lang === 'ar' ? 'اكتب ملاحظة عن آخر اجتماع/مكالمة…' : 'Write a note about latest call/meeting…'} className="bg-[#f8fafc] border-[#e5e7eb] min-h-[80px]" data-testid="note-body" />
                    <Button onClick={addNote} className="gold-button rounded-md h-auto px-4 text-xs self-start"><Plus className="w-3 h-3 me-1" /> {lang === 'ar' ? 'أضف' : 'Add'}</Button>
                  </div>
                  <div className="space-y-2">
                    {(detail?.notes || selected.notes || []).slice().reverse().map((n) => (
                      <div key={n.id} className="rounded-lg border border-[#e5e7eb] p-3 bg-[#f8fafc]">
                        <div className="flex items-center justify-between text-[10px] text-[#111827]/50 mb-1">
                          <span>{n.author_name} · {new Date(n.created_at).toLocaleString()}</span>
                        </div>
                        <div className="text-sm text-[#0a0a0a] whitespace-pre-wrap">{n.body}</div>
                      </div>
                    ))}
                    {(detail?.notes || selected.notes || []).length === 0 && <div className="text-center py-6 text-xs text-[#111827]/40">{lang === 'ar' ? 'لا ملاحظات بعد' : 'No notes yet'}</div>}
                  </div>
                  {/* Stage history */}
                  {selected.stage_history?.length > 0 && (
                    <div className="mt-6">
                      <div className="text-[10px] uppercase tracking-widest text-[#2563eb] mb-2">{lang === 'ar' ? 'سجل المراحل' : 'Stage History'}</div>
                      <div className="space-y-1">
                        {selected.stage_history.slice().reverse().map((h, i) => (
                          <div key={i} className="text-xs text-[#111827]/70">
                            <span className="text-[#2563eb] font-semibold">{stageLabel(h.stage, lang)}</span> · {new Date(h.at).toLocaleString()} <span className="text-[#111827]/40">by {h.by}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TASKS */}
              {tab === 'tasks' && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder={lang === 'ar' ? 'اتصل بالعميل يوم الأربعاء…' : 'Follow-up call on Wednesday…'} className="bg-[#f8fafc] border-[#e5e7eb]" data-testid="task-title" />
                    <Input type="datetime-local" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} className="bg-[#f8fafc] border-[#e5e7eb] w-56" />
                    <Button onClick={addTask} className="gold-button rounded-md text-xs px-4"><Plus className="w-3 h-3 me-1" /> {lang === 'ar' ? 'أضف' : 'Add'}</Button>
                  </div>
                  <div className="space-y-1.5">
                    {(detail?.tasks || selected.tasks || []).map((t) => {
                      const overdue = t.due_at && !t.done && new Date(t.due_at) < new Date();
                      return (
                        <div key={t.id} className={`flex items-center gap-3 rounded-lg border p-3 ${t.done ? 'bg-[#f8fafc] border-[#e5e7eb] opacity-60' : overdue ? 'bg-rose-50 border-rose-200' : 'bg-white border-[#e5e7eb]'}`}>
                          <button onClick={() => toggleTask(t)} className="flex-shrink-0" data-testid={`toggle-task-${t.id}`}>
                            {t.done ? <CheckSquare className="w-5 h-5 text-[#2563eb]" /> : <Square className="w-5 h-5 text-[#111827]/40" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm text-[#0a0a0a] ${t.done ? 'line-through' : ''}`}>{t.title}</div>
                            {t.due_at && <div className={`text-[10px] flex items-center gap-1 mt-0.5 ${overdue ? 'text-rose-500' : 'text-[#111827]/50'}`}><Calendar className="w-2.5 h-2.5" />{new Date(t.due_at).toLocaleString()}</div>}
                          </div>
                          <button onClick={() => removeTask(t.id)} className="text-[#111827]/40 hover:text-rose-500"><X className="w-4 h-4" /></button>
                        </div>
                      );
                    })}
                    {(detail?.tasks || selected.tasks || []).length === 0 && <div className="text-center py-6 text-xs text-[#111827]/40">{lang === 'ar' ? 'لا مهام بعد' : 'No tasks yet'}</div>}
                  </div>
                </div>
              )}

              {/* ATTACHMENTS */}
              {tab === 'attachments' && (
                <div className="space-y-3" data-testid="attachments-panel">
                  <label className={`flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#2563eb]/40 bg-[#f8fafc] py-6 px-4 cursor-pointer hover:border-[#2563eb] transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
                    <Upload className="w-4 h-4 text-[#2563eb]" />
                    <span className="text-sm text-[#2563eb] font-semibold">
                      {uploading
                        ? (lang === 'ar' ? 'جارٍ الرفع…' : 'Uploading…')
                        : (lang === 'ar' ? 'اسحب ملف هنا أو اضغط للاختيار' : 'Drop a file here or click to choose')}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(f); e.target.value = ''; }}
                      data-testid="attachment-file-input"
                    />
                  </label>
                  <div className="text-[10px] text-[#111827]/50 text-center">
                    {lang === 'ar' ? 'PDF · صور · Word · Excel · CSV · TXT · حد أقصى 5 ميجا' : 'PDF · Images · Word · Excel · CSV · TXT · max 5 MB'}
                  </div>
                  <div className="space-y-2">
                    {attachments.map((a) => (
                      <div key={a.id} className="flex items-center gap-3 rounded-lg border border-[#e5e7eb] p-3 bg-white" data-testid={`attachment-${a.id}`}>
                        <Paperclip className="w-4 h-4 text-[#2563eb] flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-[#0a0a0a] truncate font-medium">{a.filename}</div>
                          <div className="text-[10px] text-[#111827]/50">
                            {(a.size / 1024).toFixed(1)} KB · {a.uploaded_by} · {new Date(a.uploaded_at).toLocaleString()}
                          </div>
                        </div>
                        <a
                          href={crmAttachmentDownloadUrl(selected.id, a.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#2563eb] hover:text-[#1d4ed8]"
                          title={lang === 'ar' ? 'تنزيل' : 'Download'}
                          data-testid={`download-attachment-${a.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => removeAttachment(a.id)}
                          className="text-[#111827]/40 hover:text-rose-500"
                          title={lang === 'ar' ? 'حذف' : 'Delete'}
                          data-testid={`delete-attachment-${a.id}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {attachments.length === 0 && (
                      <div className="text-center py-6 text-xs text-[#111827]/40">
                        {lang === 'ar' ? 'لا مرفقات بعد' : 'No attachments yet'}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* KYC */}
              {tab === 'kyc' && (
                <div className="space-y-3" data-testid="kyc-panel">
                  <div className="flex items-center gap-2 text-sm text-[#111827]/70">
                    <ShieldCheck className="w-4 h-4 text-[#2563eb]" />
                    {lang === 'ar' ? 'فحص KYC/AML يدوي — لا يوجد فحص خارجي' : 'Manual KYC/AML — no external screening'}
                  </div>
                  {(() => {
                    const k = kyc || { status: 'pending' };
                    const upd = (patch) => setKyc({ ...k, ...patch });
                    const setBool = (key) => (e) => upd({ [key]: e.target.checked });
                    return (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold block mb-1">{lang === 'ar' ? 'الاسم الكامل' : 'Full Name'}</label>
                            <input type="text" value={k.full_name || ''} onChange={(e) => upd({ full_name: e.target.value })} className="w-full h-10 px-3 rounded-md bg-[#f8fafc] border border-[#2563eb]/25 text-[#111827] text-sm" data-testid="kyc-full-name" />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold block mb-1">{lang === 'ar' ? 'رقم جواز السفر' : 'Passport #'}</label>
                            <input type="text" value={k.passport_number || ''} onChange={(e) => upd({ passport_number: e.target.value })} className="w-full h-10 px-3 rounded-md bg-[#f8fafc] border border-[#2563eb]/25 text-[#111827] text-sm" data-testid="kyc-passport" />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold block mb-1">{lang === 'ar' ? 'الجنسية' : 'Nationality'}</label>
                            <input type="text" value={k.nationality || ''} onChange={(e) => upd({ nationality: e.target.value })} className="w-full h-10 px-3 rounded-md bg-[#f8fafc] border border-[#2563eb]/25 text-[#111827] text-sm" />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold block mb-1">{lang === 'ar' ? 'تاريخ الميلاد' : 'Date of Birth'}</label>
                            <input type="date" value={k.date_of_birth || ''} onChange={(e) => upd({ date_of_birth: e.target.value })} className="w-full h-10 px-3 rounded-md bg-[#f8fafc] border border-[#2563eb]/25 text-[#111827] text-sm" />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold block mb-1">{lang === 'ar' ? 'هوية إماراتية' : 'Emirates ID'}</label>
                            <input type="text" value={k.emirates_id || ''} onChange={(e) => upd({ emirates_id: e.target.value })} className="w-full h-10 px-3 rounded-md bg-[#f8fafc] border border-[#2563eb]/25 text-[#111827] text-sm" />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold block mb-1">{lang === 'ar' ? 'المهنة' : 'Occupation'}</label>
                            <input type="text" value={k.occupation || ''} onChange={(e) => upd({ occupation: e.target.value })} className="w-full h-10 px-3 rounded-md bg-[#f8fafc] border border-[#2563eb]/25 text-[#111827] text-sm" />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold block mb-1">{lang === 'ar' ? 'العنوان' : 'Address'}</label>
                          <textarea rows={2} value={k.address || ''} onChange={(e) => upd({ address: e.target.value })} className="w-full px-3 py-2 rounded-md bg-[#f8fafc] border border-[#2563eb]/25 text-[#111827] text-sm" />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold block mb-1">{lang === 'ar' ? 'مصدر الأموال' : 'Source of Funds'}</label>
                          <input value={k.source_of_funds || ''} onChange={(e) => upd({ source_of_funds: e.target.value })} placeholder={lang === 'ar' ? 'راتب، أعمال، استثمار…' : 'Salary, business, investment…'} className="w-full h-10 px-3 rounded-md bg-[#f8fafc] border border-[#2563eb]/25 text-[#111827] text-sm" />
                        </div>

                        <div className="rounded-md border border-[#2563eb]/25 bg-[#f8fafc] p-3 space-y-2">
                          <div className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold">{lang === 'ar' ? 'قائمة تحقق يدوية' : 'Manual Verification Checklist'}</div>
                          {[
                            ['check_passport', lang === 'ar' ? 'تم فحص جواز السفر' : 'Passport verified'],
                            ['check_emirates_id', lang === 'ar' ? 'تم فحص الهوية الإماراتية' : 'Emirates ID verified'],
                            ['check_address', lang === 'ar' ? 'تم فحص العنوان' : 'Address proof verified'],
                            ['check_source_of_funds', lang === 'ar' ? 'تم فحص مصدر الأموال' : 'Source of funds verified'],
                            ['check_pep_declaration', lang === 'ar' ? 'إقرار PEP موقّع' : 'PEP declaration signed'],
                            ['check_sanctions_manual', lang === 'ar' ? 'فحص قوائم عقوبات يدوياً (لا مطابقات)' : 'Manual sanctions check (no match)'],
                            ['check_no_adverse_media', lang === 'ar' ? 'لا تغطية إعلامية سلبية' : 'No adverse media'],
                          ].map(([key, label]) => (
                            <label key={key} className="flex items-center gap-2 text-sm text-[#0a0a0a] cursor-pointer" data-testid={`kyc-check-${key}`}>
                              <input type="checkbox" checked={!!k[key]} onChange={setBool(key)} className="w-4 h-4 accent-[#2563eb]" />
                              <span>{label}</span>
                            </label>
                          ))}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold block mb-1">{lang === 'ar' ? 'مستوى المخاطر' : 'Risk Level'}</label>
                            <select value={k.risk_level || ''} onChange={(e) => upd({ risk_level: e.target.value })} className="w-full h-10 px-3 rounded-md bg-[#f8fafc] border border-[#2563eb]/25 text-[#111827] text-sm" data-testid="kyc-risk">
                              <option value="">—</option>
                              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold block mb-1">{lang === 'ar' ? 'الحالة' : 'Status'}</label>
                            <select value={k.status || 'pending'} onChange={(e) => upd({ status: e.target.value })} className="w-full h-10 px-3 rounded-md bg-[#f8fafc] border border-[#2563eb]/25 text-[#111827] text-sm" data-testid="kyc-status">
                              <option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold block mb-1">{lang === 'ar' ? 'ملاحظات' : 'Notes'}</label>
                          <textarea rows={3} value={k.notes || ''} onChange={(e) => upd({ notes: e.target.value })} className="w-full px-3 py-2 rounded-md bg-[#f8fafc] border border-[#2563eb]/25 text-[#111827] text-sm" />
                        </div>

                        <div className="rounded-md border border-[#2563eb]/25 bg-[#f8fafc] p-3 space-y-3" data-testid="kyc-id-docs">
                          <div className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold">{lang === 'ar' ? 'مستندات الهوية (تظهر كاملة في ملف PDF)' : 'Identity Documents (embedded in PDF)'}</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {[
                              ['passport', lang === 'ar' ? 'صورة جواز السفر' : 'Passport image'],
                              ['emirates_id', lang === 'ar' ? 'صورة الهوية الإماراتية' : 'Emirates ID image'],
                            ].map(([docType, label]) => {
                              const docs = attachments.filter((a) => a.doc_type === docType);
                              return (
                                <div key={docType} className="space-y-2" data-testid={`kyc-doc-${docType}`}>
                                  <label className={`flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#2563eb]/40 bg-white py-4 px-3 cursor-pointer hover:border-[#2563eb] transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
                                    <Upload className="w-4 h-4 text-[#2563eb]" />
                                    <span className="text-xs text-[#2563eb] font-semibold">{uploading ? (lang === 'ar' ? 'جارٍ الرفع…' : 'Uploading…') : label}</span>
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept=".jpg,.jpeg,.png,.webp"
                                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(f, docType); e.target.value = ''; }}
                                      data-testid={`kyc-doc-input-${docType}`}
                                    />
                                  </label>
                                  {docs.map((a) => (
                                    <div key={a.id} className="relative rounded-lg border border-[#e5e7eb] bg-white overflow-hidden" data-testid={`kyc-doc-preview-${a.id}`}>
                                      <img src={crmAttachmentDownloadUrl(selected.id, a.id)} alt={a.filename} className="w-full h-32 object-contain bg-[#f8fafc]" />
                                      <div className="flex items-center justify-between px-2 py-1.5 text-[10px] text-[#111827]/70">
                                        <span className="truncate">{a.filename}</span>
                                        <button type="button" onClick={() => removeAttachment(a.id)} className="text-rose-600 hover:text-rose-800 ms-2" data-testid={`kyc-doc-delete-${a.id}`}>
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                          <div className="text-[10px] text-[#111827]/50">{lang === 'ar' ? 'JPG · PNG · WEBP · حد أقصى 5 ميجا' : 'JPG · PNG · WEBP · max 5 MB'}</div>
                        </div>

                        <Button onClick={saveKyc} disabled={kycSaving} className="gold-button w-full" data-testid="kyc-save">
                          <ShieldCheck className="w-4 h-4 me-2" /> {kycSaving ? '…' : (lang === 'ar' ? 'حفظ فحص KYC' : 'Save KYC')}
                        </Button>
                        {k.updated_at && <div className="text-[10px] text-[#111827]/50 text-center">Last updated: {new Date(k.updated_at).toLocaleString()} · {k.updated_by || ''}</div>}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* LINKED / 360° */}
              {tab === 'linked' && (
                <div className="space-y-4">
                  {!detail ? <div className="text-center py-6 text-xs text-[#111827]/40">…</div> : (
                    <>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="glass-card rounded-lg p-3 text-center"><div className="text-[9px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'الطلبات' : 'Applications'}</div><div className="text-xl font-bold text-[#0a0a0a] mt-1">{detail.linked_applications?.length || 0}</div></div>
                        <div className="glass-card rounded-lg p-3 text-center"><div className="text-[9px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'الإيصالات' : 'Receipts'}</div><div className="text-xl font-bold text-[#0a0a0a] mt-1">{detail.linked_receipts?.length || 0}</div></div>
                        <div className="glass-card rounded-lg p-3 text-center"><div className="text-[9px] uppercase tracking-widest text-[#2563eb]">{lang === 'ar' ? 'إجمالي المدفوع' : 'Total Paid'}</div><div className="text-xl font-bold text-[#3b82f6] mt-1">{detail.total_paid?.toLocaleString()} AED</div></div>
                      </div>
                      {detail.linked_applications?.length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-[#2563eb] mb-2 flex items-center gap-1"><FileText className="w-3 h-3" /> {lang === 'ar' ? 'الطلبات' : 'Applications'}</div>
                          {detail.linked_applications.map((a) => (
                            <Link key={a.id} to={`/admin/applications?open=${a.id}`} className="flex items-center justify-between rounded-lg border border-[#e5e7eb] p-2.5 mb-1.5 hover:border-[#2563eb] hover:bg-[#f8fafc]">
                              <div>
                                <div className="text-xs text-[#2563eb] font-mono">{a.id}</div>
                                <div className="text-sm text-[#0a0a0a]">{a.company_name || '—'}</div>
                                <div className="text-[10px] text-[#111827]/50">{a.status} · {a.zone_code || a.type}</div>
                              </div>
                              <ExternalLink className="w-3.5 h-3.5 text-[#111827]/40" />
                            </Link>
                          ))}
                        </div>
                      )}
                      {detail.linked_receipts?.length > 0 ? (
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-[#2563eb] mb-2 flex items-center justify-between">
                            <span className="flex items-center gap-1"><Receipt className="w-3 h-3" /> {lang === 'ar' ? 'الإيصالات' : 'Receipts'}</span>
                            <button onClick={() => navigate(`/admin/receipts?fromLead=${selected.id}`)} className="text-[#2563eb] hover:underline normal-case text-[11px] font-semibold flex items-center gap-1" data-testid="new-receipt-linked">
                              <Plus className="w-3 h-3" /> {lang === 'ar' ? 'إيصال جديد' : 'New Receipt'}
                            </button>
                          </div>
                          {detail.linked_receipts.map((r) => (
                            <div key={r.id} className="flex items-center justify-between rounded-lg border border-[#e5e7eb] p-2.5 mb-1.5">
                              <div>
                                <div className="text-xs text-[#2563eb] font-mono">{r.id}</div>
                                <div className="text-sm text-[#0a0a0a]">{r.description || r.customer_name}</div>
                                <div className="text-[10px] text-[#111827]/50">{r.status} · {r.payment_method}</div>
                              </div>
                              <div className="text-sm font-bold text-[#3b82f6]">{Number(r.total).toLocaleString()} {r.currency}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-lg border border-dashed border-[#e5e7eb] p-4 text-center">
                          <div className="text-xs text-[#111827]/50 mb-2">{lang === 'ar' ? 'لا إيصالات لهذا العميل بعد' : 'No receipts for this client yet'}</div>
                          <Button onClick={() => navigate(`/admin/receipts?fromLead=${selected.id}`)} className="gold-button rounded-md h-8 px-3 text-[11px]" data-testid="new-receipt-empty">
                            <Plus className="w-3 h-3 me-1" /> {lang === 'ar' ? 'إصدار أول إيصال' : 'Issue First Receipt'}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <DialogFooter className="mt-6">
                <Button onClick={removeLead} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent border-rose-400 text-rose-500"><Trash2 className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'حذف' : 'Delete'}</Button>
                <Button
                  onClick={() => {
                    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
                    window.open(`${base}/api/admin/crm/leads/${selected.id}/export.pdf?_t=${Date.now()}`, '_blank');
                  }}
                  className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent"
                  data-testid="lead-print-pdf"
                >
                  <FileDown className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'طباعة PDF' : 'Print PDF'}
                </Button>
                <Button
                  onClick={() => navigate(`/admin/receipts?fromLead=${selected.id}`)}
                  className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent"
                  data-testid="issue-receipt-from-lead"
                >
                  <Receipt className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'إصدار إيصال' : 'Issue Receipt'}
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      await crmUpdateLead(selected.id, {
                        name: selected.name, email: selected.email, phone: selected.phone,
                        company: selected.company, stage: selected.stage,
                        value_estimate: selected.value_estimate, currency: selected.currency || 'AED',
                      });
                      toast({ title: lang === 'ar' ? 'حُفظ' : 'Saved' });
                      setSelected(null); load();
                    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
                  }}
                  className="gold-button rounded-md h-10 px-5 text-xs"
                  data-testid="save-lead-btn"
                >{lang === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* New Lead Dialog */}
      <Dialog open={newLeadOpen} onOpenChange={setNewLeadOpen}>
        <DialogContent className="bg-white border-[#e5e7eb] text-[#0a0a0a] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-lead-dialog">
          <DialogHeader><DialogTitle className="text-[#0a0a0a] font-display text-xl">{lang === 'ar' ? 'عميل محتمل جديد' : 'New Lead'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase text-[#2563eb]">{lang === 'ar' ? 'الاسم *' : 'Name *'}</Label><Input value={newLeadForm.name} onChange={(e) => setNewLeadForm({ ...newLeadForm, name: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" data-testid="new-lead-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase text-[#2563eb]">{lang === 'ar' ? 'البريد' : 'Email'}</Label><Input type="email" value={newLeadForm.email} onChange={(e) => setNewLeadForm({ ...newLeadForm, email: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" /></div>
              <div><Label className="text-xs uppercase text-[#2563eb]">{lang === 'ar' ? 'الهاتف' : 'Phone'}</Label><Input value={newLeadForm.phone} onChange={(e) => setNewLeadForm({ ...newLeadForm, phone: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" /></div>
            </div>
            <div><Label className="text-xs uppercase text-[#2563eb]">{lang === 'ar' ? 'الشركة' : 'Company'}</Label><Input value={newLeadForm.company} onChange={(e) => setNewLeadForm({ ...newLeadForm, company: e.target.value })} className="mt-1 bg-[#f8fafc] border-[#e5e7eb]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase text-[#2563eb]">{lang === 'ar' ? 'المرحلة' : 'Stage'}</Label>
                <Select value={newLeadForm.stage} onValueChange={(v) => setNewLeadForm({ ...newLeadForm, stage: v })}>
                  <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e5e7eb]"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{stageLabel(s.id, lang)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase text-[#2563eb]">{lang === 'ar' ? 'القيمة المتوقعة' : 'Est. Value'}</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="number" value={newLeadForm.value_estimate} onChange={(e) => setNewLeadForm({ ...newLeadForm, value_estimate: Number(e.target.value) })} className="bg-[#f8fafc] border-[#e5e7eb] flex-1" />
                  <Select value={newLeadForm.currency} onValueChange={(v) => setNewLeadForm({ ...newLeadForm, currency: v })}>
                    <SelectTrigger data-testid="new-lead-currency" className="bg-[#f8fafc] border-[#e5e7eb] w-24"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="AED">AED</SelectItem><SelectItem value="EUR">EUR</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* KYC toggle */}
            <label className="flex items-center gap-2 pt-3 border-t border-[#e5e7eb] cursor-pointer" data-testid="new-lead-include-kyc">
              <input type="checkbox" checked={newLeadForm.include_kyc} onChange={(e) => setNewLeadForm({ ...newLeadForm, include_kyc: e.target.checked })} className="w-4 h-4 accent-[#2563eb]" />
              <ShieldCheck className="w-4 h-4 text-[#2563eb]" />
              <span className="text-sm font-semibold text-[#0a0a0a]">{lang === 'ar' ? 'إضافة بيانات KYC للعميل' : 'Add KYC / AML details'}</span>
            </label>

            {newLeadForm.include_kyc && (
              <div className="rounded-md border border-[#2563eb]/25 bg-[#f8fafc] p-3 space-y-3" data-testid="new-lead-kyc-section">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-[10px] uppercase text-[#2563eb]">{lang === 'ar' ? 'الاسم الكامل' : 'Full Name'}</Label><Input value={newLeadForm.kyc_full_name} onChange={(e) => setNewLeadForm({ ...newLeadForm, kyc_full_name: e.target.value })} className="mt-1 h-10 bg-white border-[#e5e7eb] text-sm" data-testid="new-lead-kyc-full-name" /></div>
                  <div><Label className="text-[10px] uppercase text-[#2563eb]">{lang === 'ar' ? 'رقم الجواز' : 'Passport #'}</Label><Input value={newLeadForm.kyc_passport_number} onChange={(e) => setNewLeadForm({ ...newLeadForm, kyc_passport_number: e.target.value })} className="mt-1 h-10 bg-white border-[#e5e7eb] text-sm" /></div>
                  <div><Label className="text-[10px] uppercase text-[#2563eb]">{lang === 'ar' ? 'الجنسية' : 'Nationality'}</Label><Input value={newLeadForm.kyc_nationality} onChange={(e) => setNewLeadForm({ ...newLeadForm, kyc_nationality: e.target.value })} className="mt-1 h-10 bg-white border-[#e5e7eb] text-sm" /></div>
                  <div><Label className="text-[10px] uppercase text-[#2563eb]">{lang === 'ar' ? 'تاريخ الميلاد' : 'Date of Birth'}</Label><Input type="date" value={newLeadForm.kyc_date_of_birth} onChange={(e) => setNewLeadForm({ ...newLeadForm, kyc_date_of_birth: e.target.value })} className="mt-1 h-10 bg-white border-[#e5e7eb] text-sm" /></div>
                  <div><Label className="text-[10px] uppercase text-[#2563eb]">{lang === 'ar' ? 'هوية إماراتية' : 'Emirates ID'}</Label><Input value={newLeadForm.kyc_emirates_id} onChange={(e) => setNewLeadForm({ ...newLeadForm, kyc_emirates_id: e.target.value })} className="mt-1 h-10 bg-white border-[#e5e7eb] text-sm" /></div>
                  <div><Label className="text-[10px] uppercase text-[#2563eb]">{lang === 'ar' ? 'المهنة' : 'Occupation'}</Label><Input value={newLeadForm.kyc_occupation} onChange={(e) => setNewLeadForm({ ...newLeadForm, kyc_occupation: e.target.value })} className="mt-1 h-10 bg-white border-[#e5e7eb] text-sm" /></div>
                </div>
                <div><Label className="text-[10px] uppercase text-[#2563eb]">{lang === 'ar' ? 'العنوان' : 'Address'}</Label><Input value={newLeadForm.kyc_address} onChange={(e) => setNewLeadForm({ ...newLeadForm, kyc_address: e.target.value })} className="mt-1 h-10 bg-white border-[#e5e7eb] text-sm" /></div>
                <div><Label className="text-[10px] uppercase text-[#2563eb]">{lang === 'ar' ? 'مصدر الأموال' : 'Source of Funds'}</Label><Input value={newLeadForm.kyc_source_of_funds} onChange={(e) => setNewLeadForm({ ...newLeadForm, kyc_source_of_funds: e.target.value })} className="mt-1 h-10 bg-white border-[#e5e7eb] text-sm" /></div>

                <div className="pt-2 space-y-1">
                  <div className="text-[10px] uppercase text-[#2563eb] font-bold mb-1">{lang === 'ar' ? 'قائمة تحقق' : 'Verification checklist'}</div>
                  {[
                    ['kyc_check_passport', lang === 'ar' ? 'تم فحص الجواز' : 'Passport verified'],
                    ['kyc_check_emirates_id', lang === 'ar' ? 'تم فحص الهوية الإماراتية' : 'Emirates ID verified'],
                    ['kyc_check_address', lang === 'ar' ? 'تم فحص العنوان' : 'Address proof verified'],
                    ['kyc_check_source_of_funds', lang === 'ar' ? 'تم فحص مصدر الأموال' : 'Source of funds verified'],
                    ['kyc_check_pep_declaration', lang === 'ar' ? 'إقرار PEP موقّع' : 'PEP declaration signed'],
                    ['kyc_check_sanctions_manual', lang === 'ar' ? 'فحص قوائم عقوبات يدوياً' : 'Manual sanctions check'],
                    ['kyc_check_no_adverse_media', lang === 'ar' ? 'لا تغطية إعلامية سلبية' : 'No adverse media'],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-xs text-[#0a0a0a] cursor-pointer">
                      <input type="checkbox" checked={!!newLeadForm[key]} onChange={(e) => setNewLeadForm({ ...newLeadForm, [key]: e.target.checked })} className="w-4 h-4 accent-[#2563eb]" />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] uppercase text-[#2563eb]">{lang === 'ar' ? 'المخاطر' : 'Risk'}</Label>
                    <select value={newLeadForm.kyc_risk_level} onChange={(e) => setNewLeadForm({ ...newLeadForm, kyc_risk_level: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-md bg-white border border-[#e5e7eb] text-sm"><option value="">—</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select>
                  </div>
                  <div><Label className="text-[10px] uppercase text-[#2563eb]">{lang === 'ar' ? 'الحالة' : 'Status'}</Label>
                    <select value={newLeadForm.kyc_status} onChange={(e) => setNewLeadForm({ ...newLeadForm, kyc_status: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-md bg-white border border-[#e5e7eb] text-sm"><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option></select>
                  </div>
                </div>
                <div><Label className="text-[10px] uppercase text-[#2563eb]">{lang === 'ar' ? 'ملاحظات KYC' : 'KYC Notes'}</Label><textarea rows={2} value={newLeadForm.kyc_notes} onChange={(e) => setNewLeadForm({ ...newLeadForm, kyc_notes: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md bg-white border border-[#e5e7eb] text-sm" /></div>

                <div className="grid grid-cols-2 gap-3" data-testid="new-lead-kyc-docs">
                  {[
                    ['passport', lang === 'ar' ? 'صورة الجواز' : 'Passport image'],
                    ['emirates_id', lang === 'ar' ? 'صورة الهوية' : 'Emirates ID image'],
                  ].map(([docType, label]) => (
                    <div key={docType}>
                      <Label className="text-[10px] uppercase text-[#2563eb]">{label}</Label>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp"
                        onChange={(e) => setNewLeadDocs({ ...newLeadDocs, [docType]: e.target.files?.[0] || null })}
                        className="mt-1 block w-full text-xs text-[#111827] file:me-2 file:rounded-md file:border-0 file:bg-[#2563eb] file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-[#1d4ed8]"
                        data-testid={`new-lead-kyc-doc-${docType}`}
                      />
                      {newLeadDocs[docType] && <div className="text-[10px] text-[#111827]/60 truncate mt-1">{newLeadDocs[docType].name}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setNewLeadOpen(false)} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
            <Button onClick={createLead} className="gold-button rounded-md h-10 px-5 text-xs" data-testid="create-lead-btn">
              {newLeadForm.include_kyc ? (lang === 'ar' ? 'إنشاء وطباعة PDF' : 'Create & Print PDF') : (lang === 'ar' ? 'إنشاء' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCRM;
